<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import subDays from 'date-fns/subDays';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';

const getters = useStoreGetters();
const { t, locale } = useI18n();
const dateLocale = computed(() =>
  ['pt-BR', 'pt_BR', 'pt'].includes(locale.value)
    ? 'pt-BR'
    : locale.value || 'en'
);

const selectedCampaign = ref(null);
const accountId = useMapGetter('getCurrentAccountId');
const uiFlags = useMapGetter('campaigns/getUIFlags');
const inboxes = useMapGetter('inboxes/getInboxes');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);

const selectedCampaignInbox = computed(() => {
  if (!selectedCampaign.value || !selectedCampaign.value.inbox_id) return null;
  return inboxes.value.find(i => i.id === selectedCampaign.value.inbox_id);
});

const whatsappCampaigns = computed(() => {
  const list = getters['customMessageTemplates/getTemplates'].value || [];
  return list.filter(tpl => {
    const isExclusive =
      tpl.variables?.is_exclusive_active_message === true ||
      tpl.variables?.is_exclusive_active_message === 'true';
    return !isExclusive;
  });
});

const metrics = ref(null);
const history = ref([]);
const isFetchingMetrics = ref(false);

const customDateRange = ref([subDays(new Date(), 6), new Date()]);
const selectedDateRange = ref(DATE_RANGE_TYPES.LAST_7_DAYS);

const fromDate = computed(() => getUnixStartOfDay(customDateRange.value[0]));
const toDate = computed(() => getUnixEndOfDay(customDateRange.value[1]));

const fetchMetrics = async campaign => {
  if (!campaign) return;
  isFetchingMetrics.value = true;
  try {
    const dateParams = `&since=${fromDate.value}&until=${toDate.value}`;
    const { data } = await window.axios.get(
      `/api/v1/accounts/${accountId.value}/campaign_deliveries/metrics?campaign_id=${campaign.id}&campaign_type=whatsapp${dateParams}`
    );
    metrics.value = data;

    const res = await window.axios.get(
      `/api/v1/accounts/${accountId.value}/campaign_deliveries?campaign_id=${campaign.id}&campaign_type=whatsapp${dateParams}`
    );
    history.value = res.data.deliveries;
  } catch (error) {
    useAlert(t('CAMPAIGN.WHATSAPP_STATUS.LOAD_ERROR'));
  } finally {
    isFetchingMetrics.value = false;
  }
};

const onDateRangeChange = () => {
  if (selectedCampaign.value) {
    fetchMetrics(selectedCampaign.value);
  }
};

const selectCampaign = campaign => {
  selectedCampaign.value = campaign;
  fetchMetrics(campaign);
};

const downloadCSV = async () => {
  if (!selectedCampaign.value) return;
  try {
    const dateParams = `&since=${fromDate.value}&until=${toDate.value}`;
    const fullHistory = [];
    let page = 1;
    let totalPages = 1;
    do {
      // Reports are paginated by the API; fetch each page before creating the local CSV.
      // eslint-disable-next-line no-await-in-loop
      const { data } = await window.axios.get(
        `/api/v1/accounts/${accountId.value}/campaign_deliveries?campaign_id=${selectedCampaign.value.id}&campaign_type=whatsapp&per_page=100&page=${page}${dateParams}`
      );
      fullHistory.push(...data.deliveries);
      totalPages = data.meta.total_pages;
      page += 1;
    } while (page <= totalPages);

    if (!fullHistory.length) return;
    const headers = [
      t('CAMPAIGN.WHATSAPP_STATUS.PHONE'),
      t('CAMPAIGN.WHATSAPP_STATUS.STATUS'),
      t('CAMPAIGN.WHATSAPP_STATUS.DATE'),
      t('CAMPAIGN.WHATSAPP_STATUS.ERROR'),
    ];
    const rows = fullHistory.map(h => [
      `="${h.phone_number}"`,
      h.status,
      h.created_at,
      `"${h.error_message || ''}"`,
    ]);
    const csvContent =
      '\uFEFF' + [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `relatorio_whatsapp_${selectedCampaign.value.id}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    useAlert(t('CAMPAIGN.WHATSAPP_STATUS.EXPORT_ERROR'));
  }
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
  <div
    class="flex flex-col h-full bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100 overflow-hidden w-full font-sans transition-colors duration-200"
  >
    <!-- Header Area -->
    <div
      class="flex flex-col px-8 pt-8 pb-4 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 shadow-sm z-10"
    >
      <div class="flex justify-between items-center">
        <div>
          <h1
            class="text-2xl font-semibold text-slate-800 dark:text-white tracking-tight"
          >
            {{ t('CAMPAIGN.WHATSAPP_STATUS.TITLE') }}
          </h1>
          <p class="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {{ t('CAMPAIGN.WHATSAPP_STATUS.SELECT_CAMPAIGN_METRICS') }}
          </p>
        </div>
      </div>
    </div>

    <div class="flex flex-1 overflow-hidden">
      <!-- Left Sidebar: Campaigns List -->
      <div
        class="w-[350px] border-r border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex flex-col h-full"
      >
        <div class="p-4 border-b border-slate-100 dark:border-slate-700/50">
          <h3
            class="text-xs font-semibold text-slate-500 uppercase tracking-wider"
          >
            {{ t('CAMPAIGN.WHATSAPP_STATUS.YOUR_CAMPAIGNS') }}
          </h3>
        </div>

        <div class="flex-1 overflow-y-auto p-3">
          <div v-if="isFetchingCampaigns" class="flex justify-center p-8">
            <span
              class="icon i-lucide-loader animate-spin text-2xl text-slate-400"
            />
          </div>
          <div
            v-else-if="whatsappCampaigns.length === 0"
            class="flex flex-col items-center justify-center p-8 text-center"
          >
            <div
              class="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center mb-3"
            >
              <span
                class="icon i-lucide-message-square text-xl text-slate-400"
              />
            </div>
            <p class="text-sm text-slate-500 dark:text-slate-400">
              {{ t('CAMPAIGN.WHATSAPP_STATUS.NO_CAMPAIGNS') }}
            </p>
          </div>
          <ul v-else class="space-y-2">
            <li
              v-for="campaign in whatsappCampaigns"
              :key="campaign.id"
              role="button"
              tabindex="0"
              :aria-pressed="selectedCampaign?.id === campaign.id"
              class="group p-4 rounded-xl cursor-pointer transition-all border shadow-sm"
              :class="[
                selectedCampaign?.id === campaign.id
                  ? 'bg-blue-50 border-blue-200 dark:bg-blue-900/30 dark:border-blue-800'
                  : 'bg-white border-slate-200 hover:border-blue-300 dark:bg-slate-800 dark:border-slate-700 dark:hover:border-slate-600',
              ]"
              @click="selectCampaign(campaign)"
              @keydown.enter="selectCampaign(campaign)"
              @keydown.space.prevent="selectCampaign(campaign)"
            >
              <div class="flex items-start justify-between">
                <h4
                  class="font-medium text-sm truncate pr-2"
                  :class="
                    selectedCampaign?.id === campaign.id
                      ? 'text-blue-800 dark:text-blue-300'
                      : 'text-slate-800 dark:text-slate-200'
                  "
                >
                  {{ campaign.name }}
                </h4>
                <div
                  v-if="
                    campaign.inbox_id &&
                    inboxes.find(i => i.id === campaign.inbox_id)
                  "
                  class="shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700/50 text-slate-500 dark:text-slate-400"
                >
                  {{ inboxes.find(i => i.id === campaign.inbox_id).name }}
                </div>
              </div>
              <p
                class="text-xs text-slate-500 dark:text-slate-400 mt-1.5 line-clamp-2 leading-relaxed"
              >
                {{ campaign.category }}
              </p>
            </li>
          </ul>
        </div>
      </div>

      <!-- Main Content -->
      <div class="flex-1 overflow-y-auto bg-slate-50 dark:bg-slate-900 p-8">
        <div v-if="selectedCampaign">
          <div class="flex justify-between items-center mb-8">
            <div>
              <div class="flex items-center gap-3">
                <h2 class="text-2xl font-bold text-slate-900 dark:text-white">
                  {{ selectedCampaign.name }}
                </h2>
                <div
                  v-if="selectedCampaignInbox"
                  class="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-2.5 py-1 rounded-md border border-green-100 dark:border-green-800/50 mt-0.5"
                >
                  <span class="icon i-lucide-box size-3.5" />
                  {{
                    t('CAMPAIGN.WHATSAPP_STATUS.INBOX', {
                      name: selectedCampaignInbox.name,
                    })
                  }}
                </div>
              </div>
              <p class="text-slate-500 dark:text-slate-400 mt-1">
                {{ t('CAMPAIGN.WHATSAPP_STATUS.ENGAGEMENT_OVERVIEW') }}
              </p>
            </div>
            <div class="flex items-center gap-4">
              <WootDatePicker
                v-model:date-range="customDateRange"
                v-model:range-type="selectedDateRange"
                position="right"
                @date-range-changed="onDateRangeChange"
              />
              <button
                class="px-4 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                @click="downloadCSV"
              >
                <span class="icon i-lucide-download size-4" />
                {{ t('CAMPAIGN.WHATSAPP_STATUS.EXPORT_CSV') }}
              </button>
            </div>
          </div>

          <div v-if="isFetchingMetrics" class="flex justify-center p-12">
            <span class="spinner size-8 border-slate-900 dark:border-white" />
          </div>
          <div v-else-if="metrics">
            <div class="grid grid-cols-5 gap-6 mb-8">
              <div
                class="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/50 rounded-xl p-6"
              >
                <h4
                  class="text-sm font-medium text-blue-600 dark:text-blue-400"
                >
                  {{ t('CAMPAIGN.WHATSAPP_STATUS.TOTAL_PROCESSED') }}
                </h4>
                <p
                  class="text-3xl font-bold text-slate-900 dark:text-white mt-2"
                >
                  {{ metrics.total }}
                </p>
              </div>
              <div
                class="bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800/50 rounded-xl p-6"
              >
                <h4
                  class="text-sm font-medium text-green-600 dark:text-green-400"
                >
                  {{ t('CAMPAIGN.WHATSAPP_STATUS.SENT_DELIVERED') }}
                </h4>
                <p
                  class="text-3xl font-bold text-slate-900 dark:text-white mt-2"
                >
                  {{ metrics.sent + metrics.delivered }}
                </p>
              </div>
              <div
                class="bg-violet-50 dark:bg-violet-900/20 border border-violet-100 dark:border-violet-800/50 rounded-xl p-6"
              >
                <h4
                  class="text-sm font-medium text-violet-600 dark:text-violet-400"
                >
                  {{ t('CAMPAIGN.WHATSAPP_STATUS.READ') }}
                </h4>
                <p
                  class="text-3xl font-bold text-slate-900 dark:text-white mt-2"
                >
                  {{ metrics.read }}
                </p>
              </div>
              <div
                class="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/50 rounded-xl p-6"
              >
                <h4
                  class="text-sm font-medium text-blue-600 dark:text-blue-400"
                >
                  {{ t('CAMPAIGN.WHATSAPP_STATUS.PENDING') }}
                </h4>
                <p
                  class="text-3xl font-bold text-slate-900 dark:text-white mt-2"
                >
                  {{ metrics.pending }}
                </p>
              </div>
              <div
                class="bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/50 rounded-xl p-6"
              >
                <h4 class="text-sm font-medium text-red-600 dark:text-red-400">
                  {{ t('CAMPAIGN.WHATSAPP_STATUS.FAILURES') }}
                </h4>
                <p
                  class="text-3xl font-bold text-slate-900 dark:text-white mt-2"
                >
                  {{ metrics.failed }}
                </p>
              </div>
            </div>

            <div v-if="metrics.total > 0" class="mt-8 mb-8">
              <h3
                class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3"
              >
                {{ t('CAMPAIGN.WHATSAPP_STATUS.CAMPAIGN_PROGRESS') }}
              </h3>
              <div
                class="w-full h-4 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden flex shadow-inner"
              >
                <!-- Lidos (Read) -->
                <div
                  v-if="metrics.read > 0"
                  class="h-full bg-violet-500 transition-all duration-500"
                  :style="{ width: `${(metrics.read / metrics.total) * 100}%` }"
                  :title="t('CAMPAIGN.WHATSAPP_STATUS.READ')"
                />
                <!-- Entregues -->
                <div
                  v-if="metrics.sent + metrics.delivered > 0"
                  class="h-full bg-green-500 transition-all duration-500"
                  :style="{
                    width: `${((metrics.sent + metrics.delivered) / metrics.total) * 100}%`,
                  }"
                  :title="t('CAMPAIGN.WHATSAPP_STATUS.DELIVERED')"
                />
                <!-- Pendentes -->
                <div
                  v-if="metrics.pending > 0"
                  class="h-full bg-blue-400 transition-all duration-500"
                  :style="{
                    width: `${(metrics.pending / metrics.total) * 100}%`,
                  }"
                  :title="t('CAMPAIGN.WHATSAPP_STATUS.PENDING')"
                />
                <!-- Falhas -->
                <div
                  v-if="metrics.failed > 0"
                  class="h-full bg-red-500 transition-all duration-500"
                  :style="{
                    width: `${(metrics.failed / metrics.total) * 100}%`,
                  }"
                  :title="t('CAMPAIGN.WHATSAPP_STATUS.FAILURES')"
                />
              </div>
              <div
                class="flex gap-4 mt-3 text-xs text-slate-500 dark:text-slate-400 font-medium"
              >
                <div class="flex items-center gap-1.5">
                  <span class="size-2.5 rounded-full bg-violet-500" />
                  {{ t('CAMPAIGN.WHATSAPP_STATUS.READ') }} ({{
                    ((metrics.read / metrics.total) * 100).toFixed(1)
                  }}%)
                </div>
                <div class="flex items-center gap-1.5">
                  <span class="size-2.5 rounded-full bg-green-500" />
                  {{ t('CAMPAIGN.WHATSAPP_STATUS.DELIVERED') }} ({{
                    (
                      ((metrics.sent + metrics.delivered) / metrics.total) *
                      100
                    ).toFixed(1)
                  }}%)
                </div>
                <div class="flex items-center gap-1.5">
                  <span class="size-2.5 rounded-full bg-blue-400" />
                  {{ t('CAMPAIGN.WHATSAPP_STATUS.PENDING') }} ({{
                    ((metrics.pending / metrics.total) * 100).toFixed(1)
                  }}%)
                </div>
                <div class="flex items-center gap-1.5">
                  <span class="size-2.5 rounded-full bg-red-500" />
                  {{ t('CAMPAIGN.WHATSAPP_STATUS.FAILURES') }} ({{
                    ((metrics.failed / metrics.total) * 100).toFixed(1)
                  }}%)
                </div>
              </div>
            </div>

            <h3
              class="text-lg font-semibold text-slate-900 dark:text-white mb-4"
            >
              {{ t('CAMPAIGN.WHATSAPP_STATUS.LATEST_SENDS') }}
            </h3>
            <div
              class="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden"
            >
              <table class="w-full text-left text-sm">
                <thead
                  class="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800"
                >
                  <tr>
                    <th
                      class="p-4 font-medium text-slate-500 dark:text-slate-400"
                    >
                      {{ t('CAMPAIGN.WHATSAPP_STATUS.PHONE') }}
                    </th>
                    <th
                      class="p-4 font-medium text-slate-500 dark:text-slate-400"
                    >
                      {{ t('CAMPAIGN.WHATSAPP_STATUS.STATUS') }}
                    </th>
                    <th
                      class="p-4 font-medium text-slate-500 dark:text-slate-400"
                    >
                      {{ t('CAMPAIGN.WHATSAPP_STATUS.DATE') }}
                    </th>
                    <th
                      class="p-4 font-medium text-slate-500 dark:text-slate-400"
                    >
                      {{ t('CAMPAIGN.WHATSAPP_STATUS.DETAIL') }}
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
                  <tr
                    v-for="h in history"
                    :key="h.id"
                    class="hover:bg-slate-50 dark:hover:bg-slate-800/20"
                  >
                    <td
                      class="p-4 text-slate-900 dark:text-slate-300 font-medium"
                    >
                      {{ h.phone_number }}
                    </td>
                    <td class="p-4">
                      <span
                        v-if="h.status === 'read'"
                        class="text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 px-2 py-1 rounded text-xs font-medium"
                        >{{ t('CAMPAIGN.WHATSAPP_STATUS.READ_STATUS') }}</span
                      >
                      <span
                        v-else-if="
                          h.status === 'delivered' || h.status === 'sent'
                        "
                        class="text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-2 py-1 rounded text-xs font-medium"
                        >{{
                          t('CAMPAIGN.WHATSAPP_STATUS.DELIVERED_STATUS')
                        }}</span
                      >
                      <span
                        v-else-if="h.status === 'failed'"
                        class="text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/20 px-2 py-1 rounded text-xs font-medium"
                        >{{
                          t('CAMPAIGN.WHATSAPP_STATUS.FAILURE_STATUS')
                        }}</span
                      >
                      <span
                        v-else
                        class="text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded text-xs font-medium"
                        >{{
                          t('CAMPAIGN.WHATSAPP_STATUS.PENDING_STATUS')
                        }}</span
                      >
                    </td>
                    <td class="p-4 text-slate-500 dark:text-slate-400">
                      {{ new Date(h.created_at).toLocaleString(dateLocale) }}
                    </td>
                    <td
                      class="p-4 text-slate-500 dark:text-slate-400 truncate max-w-[200px]"
                    >
                      {{ h.error_message || '-' }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div
          v-else
          class="flex flex-col items-center justify-center h-full text-center"
        >
          <div
            class="w-20 h-20 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-6 border border-slate-200 dark:border-slate-700 shadow-sm"
          >
            <span class="icon i-lucide-bar-chart-2 text-3xl text-slate-400" />
          </div>
          <h2
            class="text-xl font-medium text-slate-800 dark:text-slate-200 mb-2"
          >
            {{ t('CAMPAIGN.WHATSAPP_STATUS.REPORTS_TITLE') }}
          </h2>
          <p class="text-slate-500 dark:text-slate-400 max-w-md">
            {{ t('CAMPAIGN.WHATSAPP_STATUS.SELECT_CAMPAIGN_STATS') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
